// ═══════════════════════════════════════════════════════════════
// ModelProfileTooltip — Tooltip riche pour un profil modèle
// ITRIIA BENCH v0.1
// ═══════════════════════════════════════════════════════════════
//
// Affichage détaillé des scores, coûts et recommandations d'un
// modèle dans le SummarizerModelPicker.
//
// Props:
//   - profile: un profil du MODEL_PROFILES
//   - style: inline styles CSS optionnels pour positioning
//
// Positionnement : le composant ne gère pas position/top/left.
// Le parent gère absolute positioning.
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { Sparkles, Zap } from 'lucide-react';

function ModelProfileTooltip({ profile, style }) {
    if (!profile) return null;

    const {
        label,
        provider,
        tier_label,
        tier_color,
        bench,
        recommendation,
        strengths = [],
    } = profile;

    // Calcul du score composite (fidelity + density + structure + style + neutrality = max 25)
    const scores = [
        bench.fidelity,
        bench.density,
        bench.structure,
        bench.style,
        bench.neutrality,
    ];
    const composite = scores.reduce((a, b) => a + b, 0);

    const containerStyle = {
        position: 'relative',
        width: '320px',
        background: 'var(--card-bg)',
        border: '1px solid var(--card-border)',
        borderRadius: '12px',
        padding: '14px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
        fontSize: '13px',
        lineHeight: '1.5',
        color: 'var(--text)',
        zIndex: 1000,
        ...style,
    };

    const scoreBarContainerStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: '8px',
        marginTop: '10px',
        marginBottom: '10px',
    };

    const scoreBarItemStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '3px',
    };

    const scoreBarStyle = {
        width: '100%',
        height: '5px',
        background: 'var(--card-border)',
        borderRadius: '3px',
        overflow: 'hidden',
    };

    const scoreBarFillStyle = (value) => ({
        height: '100%',
        width: `${(value / 5) * 100}%`,
        background: tier_color,
        borderRadius: '2px',
        transition: 'width 0.15s ease',
    });

    const scoreBarLabelStyle = {
        fontSize: '10px',
        color: 'var(--text-muted)',
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
    };

    const tinyPillStyle = {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: `${tier_color}20`,
        border: `1px solid ${tier_color}40`,
        color: tier_color,
    };

    const costStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        color: 'var(--text-secondary)',
        marginTop: '8px',
    };

    const recommendationStyle = {
        marginTop: '8px',
        padding: '8px',
        background: `${tier_color}10`,
        border: `1px solid ${tier_color}30`,
        borderRadius: '6px',
        fontSize: '11px',
        lineHeight: '1.4',
        color: 'var(--text)',
        fontStyle: 'italic',
    };

    const footerStyle = {
        marginTop: '10px',
        paddingTop: '10px',
        borderTop: '1px solid var(--card-border)',
        fontSize: '10px',
        color: 'var(--text-muted)',
        textAlign: 'center',
    };

    return (
        <div style={containerStyle}>
            {/* Header — Modèle + Provider + Tier */}
            <div style={{ marginBottom: '8px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text)' }}>
                    {label}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {provider}{' '}
                    <span style={tinyPillStyle}>{tier_label}</span>
                </div>
            </div>

            {/* Score bars — 5 dimensions */}
            <div style={scoreBarContainerStyle}>
                {[
                    { label: 'Fidélité', value: bench.fidelity },
                    { label: 'Densité', value: bench.density },
                    { label: 'Structure', value: bench.structure },
                    { label: 'Style', value: bench.style },
                    { label: 'Neutralité', value: bench.neutrality },
                ].map((dim, idx) => (
                    <div key={idx} style={scoreBarItemStyle}>
                        <div style={scoreBarStyle}>
                            <div style={scoreBarFillStyle(dim.value)}></div>
                        </div>
                        <div style={scoreBarLabelStyle}>{dim.label}</div>
                    </div>
                ))}
            </div>

            {/* Composite score */}
            <div style={{ fontSize: '12px', fontWeight: 600, color: tier_color, marginBottom: '8px' }}>
                Score composite: <strong style={{ fontSize: '13px' }}>{composite}/25</strong>
            </div>

            {/* Coût par point */}
            <div style={costStyle}>
                <Zap size={13} style={{ color: tier_color }} />
                <span>${bench.cost_per_point.toFixed(3)}/point</span>
            </div>

            {/* Recommandation */}
            {recommendation && (
                <div style={recommendationStyle}>
                    {recommendation}
                </div>
            )}

            {/* Footer — Source */}
            <div style={footerStyle}>
                Source: ITRIIA BENCH 2026-04-12
            </div>
        </div>
    );
}

export default ModelProfileTooltip;
